/**
 * Where the SPA loads a docset's data from (manifest, content, search, styles, assets).
 *
 * The location is a build-time template read from `VITE_DOC_SPA_DATA_ROOT`. The `{docset}`
 * token is replaced with the docset id; a template without the token gets `/<docset>`
 * appended. Production values live in `docs/DEPLOYMENT.md`:
 *
 * | Build                | Template                          | Example for `public`             |
 * | -------------------- | --------------------------------- | -------------------------------- |
 * | local / default      | `/preview-data/{docset}`          | `/preview-data/public`           |
 * | public docs (S3)     | `/{docset}/_data`                 | `/public/_data`                  |
 * | editor preview       | `/editor/preview-data/{docset}`   | `/editor/preview-data/public`    |
 */

export const DEFAULT_DATA_ROOT_TEMPLATE = "/preview-data/{docset}";

export const DOCSET_TOKEN = "{docset}";

export type DocsetDataLocation = {
  /** Directory holding the docset's data, without a trailing slash. */
  dataRoot: string;
  /** Absolute URL of `docset-manifest.json`. */
  manifestUrl: string;
  /** `dataRoot` with a trailing slash; used as the manifest `basePath` for every resource. */
  routeBase: string;
};

/** Collapses repeated slashes and strips leading/trailing ones from a docset id. */
export function trimDocsetId(docsetId: string): string {
  return docsetId
    .trim()
    .replace(/\/+/g, "/")
    .replace(/^\/+|\/+$/g, "");
}

/** Collapses `//` runs in a path, leaving an `http(s)://` scheme intact. */
function collapseSlashes(value: string): string {
  const absolute = value.match(/^(https?:\/\/)(.*)$/i);
  if (absolute) {
    return `${absolute[1]}${absolute[2].replace(/\/{2,}/g, "/")}`;
  }
  return value.replace(/\/{2,}/g, "/");
}

/**
 * Expands a data-root template for one docset and returns the directory without a trailing
 * slash. An unset or blank template falls back to `DEFAULT_DATA_ROOT_TEMPLATE`; a template
 * that is neither absolute (`http(s)://`) nor root-relative gets a leading `/`.
 */
export function resolveDataRoot(
  docsetId: string,
  template: string | undefined = import.meta.env.VITE_DOC_SPA_DATA_ROOT
): string {
  const docset = trimDocsetId(docsetId);
  const source = (template ?? "").trim() || DEFAULT_DATA_ROOT_TEMPLATE;
  const base = source.replace(/\/+$/, "");

  const expanded = base.includes(DOCSET_TOKEN)
    ? base.split(DOCSET_TOKEN).join(docset)
    : `${base}/${docset}`;

  const rooted =
    /^https?:\/\//i.test(expanded) || expanded.startsWith("/") ? expanded : `/${expanded}`;

  return collapseSlashes(rooted).replace(/\/+$/, "");
}

/** Manifest URL and resource base for one docset, derived from the data-root template. */
export function resolveDocsetDataLocation(
  docsetId: string,
  template: string | undefined = import.meta.env.VITE_DOC_SPA_DATA_ROOT
): DocsetDataLocation {
  const dataRoot = resolveDataRoot(docsetId, template);
  const routeBase = `${dataRoot}/`;
  return {
    dataRoot,
    manifestUrl: `${routeBase}docset-manifest.json`,
    routeBase
  };
}
